import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Optional, afterNextRender, inject, signal } from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { MessageService } from 'primeng/api';
import {
  BehaviorSubject,
  Observable,
  delay,
  of,
  map,
  tap,
  from,
  first,
  combineLatest,
  switchMap,
  filter,
  take
} from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { CountryService } from '@usheru-hff/sdk/country';
import { LocationService } from '@usheru-hff/sdk/location';
import { ConsumerLead, Country, Location } from '@usheru-hff/shared/domain';

import { AuthResponse, Credentials, User } from '../entities';

const KEY_TOKEN = 'auth_token';
const KEY_REFRESH_TOKEN = 'refresh_token';
const KEY_USER_EMAIL = 'user_email';
const KEY_USERNAME = 'username';

const LOGIN_ENDPOINT = 'auth/v2/login/';
const REGISTER_ENDPOINT = 'auth/register/';
const REFRESH_ENDPOINT = 'auth/v2/token/refresh/';
const CHANGE_PASSWORD_ENDPOINT = 'auth/change-pwd/';
const FORGOT_PASSWORD_ENDPOINT = 'auth/forgot/';
const VALIDATE_EMAIL_ENDPOINT = 'consumer-lead/validate';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly locationService = inject(LocationService);
  private readonly channelService = inject(ChannelService);

  private readonly url: string;

  /**
   * Value used for debug propouses. Store the last AuthResponse returned by the server
   */
  private authResponse!: AuthResponse;

  private jwtHelper = new JwtHelperService();

  public $userStatus = new BehaviorSubject<User>(null);

  public user: User;

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private cookieService: CookieService,
    private router: Router,
    private httpClient: HttpClient,
    private countryService: CountryService,
    private messageService?: MessageService
  ) {
    this.url = env.api.url;

    afterNextRender(async () => {
      const token = this.accessToken() || this.refreshToken();
      const status = this.checkTokenStatus();
      if (token && status !== 'expired-refresh-token') {
        this.processUserFromToken(token).pipe(take(1)).subscribe();
      }
    });
  }

  // registers and automatically logs in!
  // prettier-ignore
  public register(credentials: Credentials): Observable<AuthResponse> {
    return this.httpClient
      .post<AuthResponse>(`${this.url}/${REGISTER_ENDPOINT}`, credentials, {
        headers: this.headers
      });
  }

  public getUser(): User {
    return this.$userStatus.value;
  }

  public login(credentials: Credentials): Observable<User> {
    return this.loginApi(credentials).pipe(switchMap(authResponse => this.processUserFromAuthResponse(authResponse)));
  }

  /**
   * @param processAuthUser - The {@link login} will aproach that part in a different way
   */
  public setToken(auth: AuthResponse, processAuthUser = true): AuthResponse {
    const accessTokenExpDate = this.jwtHelper.getTokenExpirationDate(auth.accessToken);
    const refreshTokenExpDate = this.jwtHelper.getTokenExpirationDate(auth.refreshToken);

    const authFixed = {
      success: true,
      msg: 'placeholder',
      jwt: auth.accessToken,
      ...auth,
      accessTokenExpDate,
      refreshTokenExpDate
    } as AuthResponse;
    this.authResponse = authFixed;

    this.cookieService.set(KEY_TOKEN, `${auth.accessToken}`, accessTokenExpDate, '/');
    this.cookieService.set(KEY_REFRESH_TOKEN, `${auth.refreshToken}`, refreshTokenExpDate, '/');

    if (processAuthUser) this.processUserFromAuthResponse(authFixed);
    return authFixed;
  }

  private processUserFromAuthResponse(tokenResponse: AuthResponse): Observable<User> {
    if (tokenResponse.success) {
      return this.processUserFromToken(tokenResponse.accessToken);
    } else {
      throw new Error('The token response is not valid');
    }
  }

  private processUserFromToken(token: string): Observable<User> {
    const user = this.userFromToken(token);
    return this.countryService.getAllCountries().pipe(
      switchMap(countries =>
        combineLatest({
          isUserLocationInitialized: this.locationService.processUserLocation(user, true),
          isUserCountryInitialized: this.locationService.processUserCountry(user, countries)
        }).pipe(
          filter(data => data?.isUserLocationInitialized && data?.isUserCountryInitialized),
          take(1),
          tap(() => {
            this.$userStatus.next(user);
          }),
          map(() => user)
        )
      )
    );
  }

  private userFromToken(token: string): User {
    const tokenBody = this.jwtHelper.decodeToken(token);
    // watch out: tokenBody.user is actually an email

    let username = tokenBody.username;
    const { avatar, email, latitude, longitude, country } = tokenBody;
    if (!username) {
      // it happens
      const index = email.indexOf('@');
      username = email.substr(0, index);
    }
    const user: User = { username, email, avatar, latitude, longitude, country };
    return user;
  }

  public accessToken(): string | undefined {
    return this.cookieService.get(KEY_TOKEN) || undefined;
  }

  public accessTokenExpTime(): number {
    const token = this.cookieService.get(KEY_TOKEN);
    if (!token) return 0;
    return this.jwtHelper.getTokenExpirationDate(token).getTime();
  }

  public refreshToken(): string | undefined {
    return this.cookieService.get(KEY_REFRESH_TOKEN) || undefined;
  }

  public refreshTokenExpTime(): number {
    const token = this.cookieService.get(KEY_REFRESH_TOKEN);
    if (!token) return 0;
    return this.jwtHelper.getTokenExpirationDate(token).getTime();
  }

  public isLoggedIn(): boolean {
    return this.$userStatus.value !== null;
  }

  public logout(redirection?: string, skipNavigation?: boolean): void {
    sessionStorage.removeItem(KEY_USER_EMAIL);
    sessionStorage.removeItem(KEY_USERNAME);

    this.cookieService.delete(KEY_TOKEN, '/');
    this.cookieService.delete(KEY_REFRESH_TOKEN, '/');

    this.$userStatus.next(null);

    this.notifyUser({
      severity: 'info',
      summary: $localize`:@@auth-service.logout.success.title:You have been logged out`,
      detail: $localize`:@@auth-service.logout.success.description:You have been logged out`
    });

    if (skipNavigation) return;

    if (this.channelService.isCommunityEnabled()) {
      return;
    }

    if (redirection) {
      this.router.navigate([redirection]);
    } else {
      this.router.navigate(['/']);
    }
  }

  public getUserUsername(): string {
    // return this.user?.username;
    return this.$userStatus.value?.username;
    // return sessionStorage.getItem(KEY_USERNAME);
  }

  public loginApi(credentials: Credentials): Observable<AuthResponse> {
    return this.httpClient
      .post<AuthResponse>(`${this.url}/${LOGIN_ENDPOINT}`, credentials, {
        headers: this.headers
      })
      .pipe(map(authResponse => this.setToken(authResponse, false)));
  }

  public refreshApi(): Observable<AuthResponse> {
    return this.httpClient.post<AuthResponse>(
      `${this.url}/${REFRESH_ENDPOINT}`,
      {},
      {
        headers: this.refreshHeaders
      }
    );
  }

  public changePassword(credentials: Credentials): Observable<AuthResponse> {
    return this.httpClient.post<AuthResponse>(`${this.url}/${CHANGE_PASSWORD_ENDPOINT}`, credentials, {
      headers: this.postHeaders
    });
  }

  public recoverPassword(email: string): Observable<AuthResponse> {
    return this.httpClient.post<AuthResponse>(
      `${this.url}/${FORGOT_PASSWORD_ENDPOINT}`,
      { email: email },
      {
        headers: this.headers
      }
    );
  }

  public isPasswordValid(): Observable<boolean> {
    return of(true).pipe(delay(500));
  }

  public arePasswordsTheSame(formGroup: FormGroup): ValidationErrors {
    const pass = formGroup.controls['password'].value;
    const secondPass = formGroup.controls['confirmPassword'].value;
    return pass === secondPass ? null : { notSamePassword: true };
  }

  public isUserLoggedIn(username: string): boolean {
    return this.isLoggedIn() && this.getUserUsername()?.toLowerCase() === username?.toLowerCase();
  }

  putEmailValidation(uuid: string): Observable<ConsumerLead> {
    return this.httpClient.put<ConsumerLead>(
      `${this.env.api.url}/${VALIDATE_EMAIL_ENDPOINT}`,
      { uuid },
      {
        headers: this.postHeaders
      }
    );
  }

  notifyUser(args: { summary: string; detail: string; severity: 'success' | 'info' | 'warn' | 'error' }): void {
    const { summary, detail, severity = 'info' } = args;
    this.messageService.add({
      severity,
      summary,
      detail
    });
  }

  /**
   * @returns
   *  * 'available-token': The token is available and is not expired
   *  * 'close-expire-token': The token is available and is about to expire in 10 min
   *  * 'expired-token': The token is expired and the refresh token is available
   *  * 'expired-refresh-token': The token is expired and the refresh token is also expired
   */
  public checkTokenStatus(): 'available-token' | 'close-expire-token' | 'expired-token' | 'expired-refresh-token' {
    // const tokenExpTime = this.accessTokenExpTime();
    const isAccessTokenExpired = this.jwtHelper.isTokenExpired(this.accessToken());
    const isRefreshTokenExpired = this.jwtHelper.isTokenExpired(this.refreshToken());

    // available token and 10 min left to access token expiration
    // this case is comment because is not necessary now but can be useful at some point
    // const nowTimestamp = new Date().getTime();
    // const tenMinTime = 10 * 60 * 1000;
    // if (nowTimestamp < tokenExpTime && nowTimestamp > tokenExpTime - tenMinTime) return 'close-expire-token';
    // available token
    if (!isAccessTokenExpired) return 'available-token';
    // expired token and refresh token is available
    if (!isRefreshTokenExpired) return 'expired-token';
    // expired token and refresh token is also expired
    return 'expired-refresh-token';
  }

  get headers(): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Basic ' + btoa(this.env.api.user + ':' + this.env.api.pass),
      'USH-Channel': this.env.api.ushChannel
    });
  }

  get postHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Basic ' + btoa(this.env.api.user + ':' + this.env.api.pass),
      'USH-Channel': this.env.api.ushChannel,
      'Content-Type': 'application/json'
    });
  }

  get refreshHeaders() {
    return new HttpHeaders({
      Authorization: `Bearer ${this.refreshToken()}`,
      'USH-Channel': this.env.api.ushChannel,
      'Content-Type': 'application/json'
    });
  }
}
